import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Search, CheckCircle, Clock, SkipForward, Car, Loader2, X, Camera, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Confetti celebration for customer!
const celebrateReady = () => {
  // First burst
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']
  });
  // Second burst after small delay
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: ['#3b82f6', '#22c55e', '#f59e0b']
    });
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: ['#3b82f6', '#22c55e', '#f59e0b']
    });
  }, 250);
};

const STAGE_ICONS = {
  repairs: "🔧",
  prep: "🎨",
  painted: "✨",
  assembled: "🔩",
  correction: "💎",
  ready: "🏁",
};

export default function TrackJobPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [trackingCode, setTrackingCode] = useState(searchParams.get("code") || "");
  const [job, setJob] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      setTrackingCode(code);
      lookupJob(code);
    }
  }, [searchParams]);

  const lookupJob = async (code) => {
    if (!code || code.length < 6) {
      toast.error("Please enter a valid 6-character tracking code");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/jobs/${code.toUpperCase()}`);
      setJob(res.data.job);
      setStages(res.data.stages);
      
      // Log customer view (fire and forget - don't block UI)
      // Skip logging if this is an admin preview
      const isAdminPreview = searchParams.get("admin_preview") === "true";
      if (!isAdminPreview) {
        axios.post(`${API}/jobs/${code.toUpperCase()}/view`, {
          user_agent: navigator.userAgent,
          referrer: document.referrer
        }).catch(() => {}); // Silently ignore errors
      }
      
      // Check if "ready" stage is completed - celebrate!
      const readyStage = res.data.job?.stages?.ready;
      if (readyStage?.status === "completed") {
        setTimeout(() => celebrateReady(), 500);
      }
    } catch (err) {
      setJob(null);
      toast.error(err.response?.data?.detail || "Job not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    lookupJob(trackingCode);
  };

  const getStageStatus = (stageId) => {
    if (!job) return "pending";
    return job.stages[stageId]?.status || "pending";
  };

  const getCompletedCount = () => {
    if (!job) return 0;
    return Object.values(job.stages).filter(s => s.status === "completed" || s.status === "skipped").length;
  };

  // Helper to get photos array (handles old single photo format)
  const getStagePhotos = (stageData) => {
    if (!stageData) return [];
    if (stageData.photos && Array.isArray(stageData.photos)) return stageData.photos;
    if (stageData.photo) return [stageData.photo];
    return [];
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-20" data-testid="track-job-page">
      <div className="max-w-[800px] mx-auto px-6 md:px-12">
        {/* Back navigation */}
        <button onClick={() => navigate("/")} data-testid="track-back-btn"
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm tracking-[0.15em] uppercase font-body transition-colors bg-transparent border-none cursor-pointer mb-12">
          <ArrowLeft size={16} />
          Back to Home
        </button>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
            <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">Live Updates</span>
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
          </div>
          <h1 data-testid="track-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-[0.95]">
            Track Your Job
          </h1>
          <p className="text-zinc-500 font-body text-base mt-4">Enter your tracking code to see real-time progress on your vehicle.</p>
        </motion.div>

        {/* Search Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-12">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={trackingCode}
                onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                placeholder="Enter tracking code (e.g. ABC123)"
                maxLength={6}
                data-testid="tracking-code-input"
                className="w-full bg-[#09090b] border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 text-white font-mono text-xl tracking-[0.3em] placeholder:text-zinc-700 placeholder:tracking-normal placeholder:text-base text-center uppercase transition-colors"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="tracking-submit-btn"
              className="bg-white text-black font-heading text-sm tracking-[0.15em] uppercase px-8 py-4 hover:bg-zinc-200 transition-colors cursor-pointer border-none disabled:opacity-50 flex items-center gap-2">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              Track
            </button>
          </form>
        </motion.div>

        {/* Job Status */}
        {job && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            {/* Vehicle Info */}
            <div className="bg-[#09090b] border border-white/5 p-6 md:p-8 mb-[1px]">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-14 h-14 bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center shrink-0">
                    <Car size={24} className="text-[#3b82f6]" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-heading text-xl sm:text-2xl uppercase tracking-wide text-white">{job.vehicle}</h2>
                    <p className="text-zinc-500 font-body text-sm truncate">{job.service} for {job.customer_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                  <span className={`px-3 py-1 text-xs uppercase font-mono border whitespace-nowrap ${
                    job.status === "completed" ? "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/30" : "bg-[#3b82f6]/20 text-[#3b82f6] border-[#3b82f6]/30"
                  }`}>
                    {job.status === "completed" ? "Complete" : "In Progress"}
                  </span>
                  <p className="text-zinc-600 font-mono text-xs">{getCompletedCount()}/{stages.length} stages</p>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-[#09090b] border border-white/5 p-6 mb-[1px]">
              <div className="h-2 bg-white/5 overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(getCompletedCount() / stages.length) * 100}%` }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="h-full bg-gradient-to-r from-[#3b82f6] to-[#22c55e]"
                />
              </div>
            </div>

            {/* Stages */}
            <div className="space-y-[1px]">
              {stages.map((stage, index) => {
                const status = getStageStatus(stage.id);
                const stageData = job.stages[stage.id];
                const isCompleted = status === "completed";
                const isSkipped = status === "skipped";
                const isCurrent = job.current_stage === stage.id && !isCompleted && !isSkipped;
                
                return (
                  <motion.div
                    key={stage.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    data-testid={`stage-${stage.id}`}
                    className={`bg-[#09090b] border border-white/5 p-6 ${isCurrent ? 'border-l-4 border-l-[#3b82f6]' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 flex items-center justify-center text-2xl ${
                        isCompleted ? 'bg-[#22c55e]/10 border border-[#22c55e]/20' :
                        isSkipped ? 'bg-zinc-800 border border-zinc-700' :
                        isCurrent ? 'bg-[#3b82f6]/10 border border-[#3b82f6]/20 animate-pulse' :
                        'bg-white/5 border border-white/10'
                      }`}>
                        {isCompleted ? <CheckCircle size={24} className="text-[#22c55e]" /> :
                         isSkipped ? <SkipForward size={20} className="text-zinc-600" /> :
                         isCurrent ? <Clock size={20} className="text-[#3b82f6]" /> :
                         <span className="opacity-30">{STAGE_ICONS[stage.id]}</span>
                        }
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-heading text-lg uppercase tracking-wide ${
                          isCompleted ? 'text-[#22c55e]' :
                          isSkipped ? 'text-zinc-600 line-through' :
                          isCurrent ? 'text-white' : 'text-zinc-600'
                        }`}>
                          {stage.name}
                        </h3>
                        {stageData?.completed_at && (
                          <p className="text-zinc-600 font-mono text-xs mt-1">
                            {isSkipped ? 'Skipped' : 'Completed'} {new Date(stageData.completed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                        {isCurrent && (
                          <p className="text-[#3b82f6] font-body text-sm mt-1">Currently in progress...</p>
                        )}
                        {stageData?.notes && (
                          <p className="text-zinc-500 font-body text-sm mt-2">{stageData.notes}</p>
                        )}
                        {/* Photos row */}
                        {getStagePhotos(stageData).length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {getStagePhotos(stageData).map((photo, idx) => (
                              <button 
                                key={idx}
                                onClick={() => setLightboxPhoto({ photos: getStagePhotos(stageData), index: idx })}
                                className="w-14 h-14 border border-white/10 hover:border-[#3b82f6]/50 transition-colors overflow-hidden cursor-pointer bg-transparent p-0 relative group"
                              >
                                <img src={photo} alt={`${stage.name} ${idx + 1}`} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Camera size={12} className="text-white" />
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Contact Note */}
            <div className="bg-[#09090b] border border-white/5 p-6 mt-[1px] text-center">
              <p className="text-zinc-500 font-body text-sm">
                Questions about your job? Text us anytime at{" "}
                <a href="sms:0450645926" className="text-[#3b82f6] hover:text-white transition-colors no-underline">0450 645 926</a>
              </p>
            </div>
          </motion.div>
        )}

        {/* Not Found */}
        {searched && !job && !loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#09090b] border border-white/5 p-12 text-center">
            <p className="text-zinc-500 font-body text-base mb-4">No job found with that tracking code.</p>
            <p className="text-zinc-600 font-body text-sm">
              Double-check your code or{" "}
              <a href="sms:0450645926" className="text-[#3b82f6] hover:text-white transition-colors no-underline">text us</a>
              {" "}if you need help.
            </p>
          </motion.div>
        )}

        {/* Photo Lightbox with navigation and swipe support */}
        <AnimatePresence>
          {lightboxPhoto && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={() => setLightboxPhoto(null)}
              onTouchStart={(e) => {
                if (lightboxPhoto.photos?.length > 1) {
                  e.currentTarget.dataset.touchStartX = e.touches[0].clientX;
                }
              }}
              onTouchEnd={(e) => {
                if (lightboxPhoto.photos?.length > 1) {
                  const startX = parseFloat(e.currentTarget.dataset.touchStartX);
                  const endX = e.changedTouches[0].clientX;
                  const diff = startX - endX;
                  if (Math.abs(diff) > 50) {
                    if (diff > 0) {
                      // Swipe left - next
                      setLightboxPhoto(p => ({
                        ...p,
                        index: p.index === p.photos.length - 1 ? 0 : p.index + 1
                      }));
                    } else {
                      // Swipe right - prev
                      setLightboxPhoto(p => ({
                        ...p,
                        index: p.index === 0 ? p.photos.length - 1 : p.index - 1
                      }));
                    }
                  }
                }
              }}
            >
              {/* Close button */}
              <button 
                onClick={() => setLightboxPhoto(null)} 
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors bg-black/50 border-none cursor-pointer z-10 p-2"
              >
                <X size={28} />
              </button>
              
              {/* Photo counter */}
              {lightboxPhoto.photos && lightboxPhoto.photos.length > 1 && (
                <div className="absolute top-4 left-4 text-white/70 font-mono text-sm bg-black/50 px-3 py-1">
                  {lightboxPhoto.index + 1} / {lightboxPhoto.photos.length}
                </div>
              )}
              
              {/* Navigation arrows - larger touch targets */}
              {lightboxPhoto.photos && lightboxPhoto.photos.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxPhoto(p => ({
                        ...p,
                        index: p.index === 0 ? p.photos.length - 1 : p.index - 1
                      }));
                    }}
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/60 hover:bg-black/80 p-3 sm:p-4 border-none cursor-pointer transition-all z-10"
                  >
                    <ChevronLeft size={32} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxPhoto(p => ({
                        ...p,
                        index: p.index === p.photos.length - 1 ? 0 : p.index + 1
                      }));
                    }}
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/60 hover:bg-black/80 p-3 sm:p-4 border-none cursor-pointer transition-all z-10"
                  >
                    <ChevronRight size={32} />
                  </button>
                </>
              )}
              
              {/* Main image */}
              <motion.img
                key={lightboxPhoto.index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                src={lightboxPhoto.photos ? lightboxPhoto.photos[lightboxPhoto.index] : lightboxPhoto}
                alt="Stage progress"
                className="max-w-[90vw] max-h-[80vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Dot indicators */}
              {lightboxPhoto.photos && lightboxPhoto.photos.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                  {lightboxPhoto.photos.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setLightboxPhoto(p => ({ ...p, index: i })); }}
                      className={`w-2.5 h-2.5 rounded-full transition-colors border-none cursor-pointer ${i === lightboxPhoto.index ? 'bg-white' : 'bg-white/30 hover:bg-white/50'}`}
                    />
                  ))}
                </div>
              )}
              
              {/* Swipe hint for mobile */}
              {lightboxPhoto.photos && lightboxPhoto.photos.length > 1 && (
                <p className="absolute bottom-16 left-1/2 -translate-x-1/2 text-white/40 text-xs font-body sm:hidden">
                  Swipe to navigate
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
